import React, { useEffect, useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { FormError } from '../FormElements/FormElements';
import { Icon } from '../Icon/Icon';
import { Input } from '../Input/Input';

import styles from './KeyValueInput.module.scss';

type HeaderRow = {
  id: number;
  key: string;
  value: string;
};

interface KeyValueInputProps {
  name: string;
  control: Control<any>;
  error?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}

interface KeyValueInputFieldProps {
  value: string;
  onChange: (nextValue: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}

const parseHeadersToRows = (rawValue: string): HeaderRow[] => {
  const trimmed = rawValue?.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    const entries = Object.entries(parsed as Record<string, unknown>).map(([key, value], index) => ({
      id: index + 1,
      key,
      value: String(value ?? ''),
    }));
    return entries;
  } catch {
    return [];
  }
};

const rowsToJson = (rows: HeaderRow[]): string => {
  const payload: Record<string, string> = {};
  rows.forEach(row => {
    const key = row.key.trim();
    if (!key) return;
    payload[key] = row.value;
  });
  return Object.keys(payload).length > 0 ? JSON.stringify(payload) : '';
};

const KeyValueInputField: React.FC<KeyValueInputFieldProps> = ({
  value = '',
  onChange,
  onBlur,
  keyPlaceholder = 'Header key',
  valuePlaceholder = 'Header value',
}) => {
  const [rows, setRows] = useState<HeaderRow[]>(() => parseHeadersToRows(value));
  const nextRowIdRef = useRef(rows.length + 1);

  useEffect(() => {
    const serializedRows = rowsToJson(rows);
    const normalizedValue = value.trim();
    if (serializedRows === normalizedValue) return;
    const parsedRows = parseHeadersToRows(value);
    setRows(parsedRows);
    nextRowIdRef.current = parsedRows.length + 1;
  }, [value, rows]);

  const syncRows = (nextRows: HeaderRow[]) => {
    setRows(nextRows);
    onChange(rowsToJson(nextRows));
  };

  const updateRow = (id: number, fieldName: 'key' | 'value', fieldValue: string) => {
    syncRows(rows.map(row => (row.id === id ? { ...row, [fieldName]: fieldValue } : row)));
  };

  const addRow = () => {
    syncRows([...rows, { id: nextRowIdRef.current, key: '', value: '' }]);
    nextRowIdRef.current += 1;
  };

  const removeRow = (id: number) => {
    const nextRows = rows.filter(row => row.id !== id);
    syncRows(nextRows);
  };

  if (rows.length === 0) {
    return (
      <button
        type="button"
        onClick={addRow}
        className={styles.headerInitialAddAction}
        aria-label="Add row"
        title="Add"
      >
        <Icon name="add" />
      </button>
    );
  }

  return (
    <div className={styles.headersEditor}>
      {rows.map(row => (
        <div
          key={row.id}
          className={styles.headersRow}
        >
          <Input
            props={{
              value: row.key,
              placeholder: keyPlaceholder,
              id: `headerKey-${row.id}`,
              onChange: e => updateRow(row.id, 'key', e.target.value),
              onBlur,
            }}
          />
          <Input
            props={{
              value: row.value,
              placeholder: valuePlaceholder,
              id: `headerValue-${row.id}`,
              onChange: e => updateRow(row.id, 'value', e.target.value),
              onBlur,
            }}
          />
          <button
            type="button"
            onClick={() => removeRow(row.id)}
            className={styles.headerRowAction}
            aria-label="Remove row"
            title="Remove"
          >
            <Icon name="delete" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className={styles.headerInlineAddAction}
        aria-label="Add row"
        title="Add"
      >
        <Icon name="add" />
      </button>
    </div>
  );
};

export const KeyValueInput: React.FC<KeyValueInputProps> = ({
  name,
  control,
  error,
  keyPlaceholder,
  valuePlaceholder,
}) => (
  <>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <KeyValueInputField
          value={field.value || ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          keyPlaceholder={keyPlaceholder}
          valuePlaceholder={valuePlaceholder}
        />
      )}
    />
    {error && <FormError error={error} />}
  </>
);
